import { CircleNotchIcon } from '@phosphor-icons/react';
import { cn, Tooltip } from '@skupreme/shared/ui';
import { DateFormat, DateUtility } from '@utility/date';
import {
  formatDeadline,
  getAgeTone,
  getDeadline,
  type AgeTone,
} from '../../lib/age';

export type OrderAgeCellProps = {
  /** When the marketplace handed the order to us. */
  acknowledgedAt?: string;
  /** Colours the age; off for orders that already shipped. */
  isCounting?: boolean;
  isProcessing?: boolean;
  /** The marketplace's shipping deadline; the list endpoint rarely projects it. */
  shipBy?: string;
};

const TONES: Record<AgeTone, string> = {
  calm: 'sk:text-success/80',
  notice: 'sk:text-warning',
  late: 'sk:text-caution',
  overdue: 'sk:text-danger',
};

function shipByText(shipBy?: string) {
  if (!shipBy) {
    return null;
  }
  const due = new Date(shipBy);

  if (Number.isNaN(due.getTime())) {
    return null;
  }

  const deadline = getDeadline(due, new Date());
  const exactDeadline = DateUtility.formatDate(
    shipBy,
    DateFormat.meridianTimeDate,
  );

  return `Ship ${formatDeadline(deadline)} · ${exactDeadline}`;
}

export function OrderAgeCell({
  acknowledgedAt,
  isCounting,
  isProcessing,
  shipBy,
}: OrderAgeCellProps) {
  if (isProcessing) {
    return (
      <span className="sk:flex sk:items-center sk:gap-1.5 sk:text-fg-secondary">
        <CircleNotchIcon size={14} className="sk:animate-spin" />
        Processing
      </span>
    );
  }

  if (!acknowledgedAt) {
    return <span className="sk:text-fg-tertiary">—</span>;
  }

  const tone = getAgeTone(DateUtility.noOfDays(acknowledgedAt));
  const deadline = shipByText(shipBy);

  const age = (
    <span className="sk:flex sk:flex-col">
      <span
        className={
          isCounting ? cn('sk:font-medium', TONES[tone]) : 'sk:font-medium'
        }
      >
        {DateUtility.hourMinuteTime(acknowledgedAt)}
      </span>
      <span className="sk:text-xs sk:text-fg-tertiary">
        {DateUtility.formatDate(acknowledgedAt, DateFormat.meridianTimeDate)}
      </span>
    </span>
  );

  return deadline ? <Tooltip content={deadline}>{age}</Tooltip> : age;
}
